import { differenceInMinutes, format } from "date-fns";
import {
  AltTransIncidentArrival,
  AltTransIncidentArrivalStatus,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { FC } from "react";
import { HStack, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";
import { DefaultTheme } from "styled-components/dist/types";

type IncidentArrivalProps = {
  incidentArrival: AltTransIncidentArrival;
  showLabel?: boolean;
};

function getLabelForArrivalStatus(status: AltTransIncidentArrivalStatus) {
  switch (status) {
    case AltTransIncidentArrivalStatus.APPROACHING_INCIDENT:
      return "Ankomst";
    case AltTransIncidentArrivalStatus.AT_INCIDENT:
      return "Stillestående";
    case AltTransIncidentArrivalStatus.DEPARTED_INCIDENT:
      return "Passert";
    case AltTransIncidentArrivalStatus.UNKNOWN:
      return "Planlagt";
  }
}

function getColorForDiff(diff: number, theme: DefaultTheme) {
  if (diff < 0) return theme.colorInfo;
  if (diff < 4) return theme.colorSuccess;
  if (diff < 6) return theme.colorWarning;
  if (diff < 10) return theme.colorSecondaryAlarm;
  return theme.colorAlarm;
}

export const IncidentArrival: FC<IncidentArrivalProps> = ({
  incidentArrival: { actualTime, estimatedTime, scheduledTime, status },
  showLabel,
}) => {
  const theme = useTheme();

  const bestCurrentTime = actualTime ?? estimatedTime;

  const formattedScheduledTime = scheduledTime
    ? format(scheduledTime, "HH:mm")
    : null;
  const formattedCurrentTime = bestCurrentTime
    ? format(bestCurrentTime, "HH:mm")
    : null;
  const diff =
    bestCurrentTime &&
    scheduledTime &&
    differenceInMinutes(bestCurrentTime, scheduledTime);

  return (
    <>
      {showLabel && (
        <Text variant="sm" fontWeight="bold">
          {getLabelForArrivalStatus(status)}
        </Text>
      )}
      <HStack gap="0.5">
        {formattedScheduledTime ? (
          <Text
            textDecoration={diff ? "line-through" : "initial"}
            color={diff ? theme.colorTextDisabled : undefined}
          >
            {formattedScheduledTime}
          </Text>
        ) : null}
        {diff ? (
          <>
            <Text variant="sm" color={theme.colorTextDisabled}>
              |
            </Text>
            <Text>{formattedCurrentTime}</Text>
            <Text variant="sm" color={theme.colorTextDisabled}>
              |
            </Text>
            <Text color={getColorForDiff(diff, theme)} fontWeight={700}>
              {diff > 0 ? `+${diff}` : diff}
            </Text>
          </>
        ) : undefined}
      </HStack>
    </>
  );
};
